import React, { useState, useRef, useEffect } from 'react';
import { Calendar } from 'react-date-range';
import { format, parse, isValid } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { FiCalendar } from 'react-icons/fi';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './us-date-picker.css';

interface USDatePickerProps {
  value: string; // formato: YYYY-MM-DD
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  title?: string;
}

const USDatePicker: React.FC<USDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  required = false,
  className = '',
  title,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Convertir valor ISO (YYYY-MM-DD) a formato de visualización MM/DD/YYYY
  useEffect(() => {
    if (value) {
      try {
        const parsed = parse(value, 'yyyy-MM-dd', new Date());
        if (isValid(parsed)) {
          setDisplayValue(format(parsed, 'MM/dd/yyyy', { locale: enUS }));
        } else {
          setDisplayValue(value);
        }
      } catch {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateSelect = (date: Date) => {
    const isoDate = format(date, 'yyyy-MM-dd');
    onChange(isoDate);
    setIsOpen(false);
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  const selectedDate = value && isValid(parse(value, 'yyyy-MM-dd', new Date())) 
    ? parse(value, 'yyyy-MM-dd', new Date()) 
    : new Date();

  return (
    <div ref={containerRef} className={`us-date-picker ${className}`} style={{ position: 'relative' }}>
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          value={displayValue}
          readOnly
          placeholder={placeholder}
          onClick={handleInputClick}
          className="field-input date-input"
          required={required}
          title={title}
          style={{ cursor: 'pointer', paddingRight: '40px' }}
        />
        <FiCalendar
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: '#6b7280',
            fontSize: '18px'
          }}
        />
      </div>
      {isOpen && (
        <div className="us-date-picker-calendar">
          <Calendar
            date={selectedDate}
            onChange={handleDateSelect}
            color="#6366f1"
          />
        </div>
      )}
    </div>
  );
};

export default USDatePicker;
